/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Bug, Gift, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function ReleaseNotesPage() {
    return (
        <div className="container mx-auto max-w-4xl py-12">
            <div className="flex items-center justify-between mb-6">
                <div className="space-y-1">
                    <h1 className="text-3xl font-bold tracking-tight">Release Notes</h1>
                    <p className="text-muted-foreground">
                        Track the latest updates, improvements, and bug fixes to our product.
                    </p>
                </div>
            </div>

            <ReleaseNote
                date="April 11, 2025"
                description="Performance"
                changes={[
                    {
                        type: 'improvement',
                        title: 'Faster runs',
                        description: 'Tests now run 25% faster on average, and 2x faster for simple actions',
                    },
                ]}
            />
        </div>
    );
}

interface Change {
    type: 'feature' | 'improvement' | 'bugfix';
    title: string;
    description: string;
}

interface ReleaseNoteProps {
    date: string;
    description: string;
    changes: Change[];
    isMajor?: boolean;
}

const ReleaseNote = ({ date, description, changes, isMajor = false }: ReleaseNoteProps) => {
    const getChangeIcon = (type: Change['type']) => {
        switch (type) {
            case 'feature':
                return <Gift className="h-5 w-5 text-green-500" />;
            case 'improvement':
                return <Sparkles className="h-5 w-5 text-blue-500" />;
            case 'bugfix':
                return <Bug className="h-5 w-5 text-red-500" />;
        }
    };

    const getChangeTitle = (type: Change['type']) => {
        switch (type) {
            case 'feature':
                return 'New Features';
            case 'improvement':
                return 'Improvements';
            case 'bugfix':
                return 'Bug Fixes';
        }
    };

    // Group changes by type
    const groupedChanges = changes.reduce(
        (acc, change) => {
            if (!acc[change.type]) {
                acc[change.type] = [];
            }
            acc[change.type].push(change);
            return acc;
        },
        {} as Record<Change['type'], Change[]>,
    );

    return (
        <Card className={isMajor ? 'border-2 border-primary/50' : ''}>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {/* <CardTitle className="text-xl">
              Version {version}
              {isMajor && (
                <Badge className="ml-2 bg-primary">Major Release</Badge>
              )}
            </CardTitle> */}
                        <Badge variant="outline">{date}</Badge> {description}
                    </div>
                    {/* <Button variant="ghost" size="sm">
            <Wrench className="mr-2 h-4 w-4" />
            View Changelog
          </Button> */}
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {Object.entries(groupedChanges).map(([type, changes]) => (
                    <div key={type} className="space-y-3">
                        <div className="flex items-center gap-2">
                            {getChangeIcon(type as Change['type'])}
                            <h3 className="font-semibold">{getChangeTitle(type as Change['type'])}</h3>
                        </div>
                        <Separator />
                        <ul className="space-y-3">
                            {changes.map((change, index) => (
                                <li key={index} className="grid gap-1">
                                    <h4 className="font-medium">{change.title}</h4>
                                    <p className="text-sm text-muted-foreground">{change.description}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};
